// Attachment chip (DESIGN.md 10.3). Used in compose (removable, upload state)
// and detail (read-only display). Thumbnail icon by type, name (ellipsis),
// size (mono), remove button (44x44 hit area), progress / rejected states.

import type { Attachment } from '../lib/types';
import { humanSize } from '../lib/format';
import { ImageIcon, FilePdf, FileText, X } from '../lib/icons';

export type AttachState = 'uploading' | 'done' | 'rejected';

interface Props {
  name: string;
  size: number;
  mime: string;
  state?: AttachState;
  reason?: string;
  progress?: number; // 0..100 while uploading
  onRemove?: () => void;
}

function thumbIcon(mime: string) {
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf') return FilePdf;
  return FileText;
}

export function AttachmentChip({ name, size, mime, state = 'done', reason, progress, onRemove }: Props) {
  const Icon = thumbIcon(mime);
  const rejected = state === 'rejected';
  return (
    <div className={`attach-row${rejected ? ' attach-rejected' : ''}`}>
      <span className="attach-thumb" aria-hidden="true">
        <Icon weight="regular" />
      </span>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div className="attach-name" title={name}>{name}</div>
        <div className="attach-size">{humanSize(size)}</div>
        {state === 'uploading' && (
          <div className="attach-progress" aria-hidden="true">
            <div className="attach-progress-bar" style={{ width: `${progress ?? 0}%` }} />
          </div>
        )}
        {rejected && reason && (
          <div className="field-error" style={{ marginTop: '0.25rem' }}>
            <span>{reason}</span>
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          className="btn btn-icon"
          aria-label={`移除附件 ${name}`}
          onClick={onRemove}
        >
          <X weight="regular" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// Read-only display variant for the detail screen.
export function AttachmentList({ attachments }: { attachments: Attachment[] }) {
  if (attachments.length === 0) return null;
  return (
    <div className="stack-sm" aria-label="附件">
      {attachments.map((a) => (
        <AttachmentChip key={a.id} name={a.name} size={a.size} mime={a.mime} />
      ))}
    </div>
  );
}
